import { useCallback, useEffect, useRef } from "react";
import { useApiWithHandling } from "../hooks/useApiWithHandling";

export type MiniPackGenerationSourceScope = "airframe" | "assembly" | "subAssembly";

export interface MiniPackGenerationDocumentRequest {
  documentId: string;
  fileName: string;
  displayName?: string;
}

export interface MiniPackGenerationSectionRequest {
  sectionKey: string;
  sectionName: string;
  documents: MiniPackGenerationDocumentRequest[];
}

export interface MiniPackGenerationRequest {
  sourceScope: MiniPackGenerationSourceScope;
  tailNumber?: string;
  assemblyType?: string;
  assemblyFolder?: string;
  assemblyComponentId?: string;
  subAssemblyId?: string;
  sections: MiniPackGenerationSectionRequest[];
}

export interface MiniPackGenerationJobDto {
  jobId: string;
  status: number;
  progressPercent: number;
  sourceScope?: string;
  scopeLabel?: string;
  jobType?: string;
  fileName?: string;
  fileSizeBytes?: number;
  downloadUrl?: string;
  errorMessage?: string;
  sectionCount: number;
  documentCount: number;
  createdAtUtc: string;
  startedAtUtc?: string;
  completedAtUtc?: string;
  expiresAtUtc?: string;
}

export const useMiniPackGenerationService = () => {
  const { get, post, getBlob } = useApiWithHandling();
  const downloadCacheRef = useRef<Record<string, { blob: Blob; fileName: string }>>({});

  useEffect(() => {
    return () => {
      downloadCacheRef.current = {};
    };
  }, []);

  const listJobs = useCallback(async (take = 200) => {
    const response = await get<any>(`api/mini-pack-generations?take=${take}`, {
      showLoader: false,
      showErrorSnackbar: true,
      throwOnError: true,
    });

    const data = response?.data;
    if (Array.isArray(data)) {
      return data as MiniPackGenerationJobDto[];
    }

    return [] as MiniPackGenerationJobDto[];
  }, [get]);

  const createJob = useCallback(async (request: MiniPackGenerationRequest) => {
    const response = await post<any>("api/mini-pack-generations", request, {
      showLoader: false,
      showErrorSnackbar: true,
      throwOnError: true,
    });

    return response?.data as MiniPackGenerationJobDto;
  }, [post]);

  const retryJob = useCallback(async (jobId: string) => {
    const response = await post<any>(`api/mini-pack-generations/${jobId}/retry`, {}, {
      showLoader: false,
      showErrorSnackbar: true,
      throwOnError: true,
    });

    return response?.data as MiniPackGenerationJobDto;
  }, [post]);

  const downloadJob = useCallback(async (job: MiniPackGenerationJobDto) => {
    const cached = downloadCacheRef.current[job.jobId];
    if (cached) {
      const objectUrl = URL.createObjectURL(cached.blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = cached.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      return true;
    }

    const response = await getBlob(`api/mini-pack-generations/${job.jobId}/download`, {
      showLoader: false,
      showErrorSnackbar: false,
      showSuccessSnackbar: false,
    });

    if (!response) {
      return false;
    }

    const fileName = response.fileName?.trim() || job.fileName?.trim() || "mini-pack.pdf";
    downloadCacheRef.current[job.jobId] = { blob: response.blob, fileName };

    const objectUrl = URL.createObjectURL(response.blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

    return true;
  }, [getBlob]);

  return {
    listJobs,
    createJob,
    retryJob,
    downloadJob,
  };
};
